import { useEffect } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import MarketingSiteHeader from './components/MarketingSiteHeader';
import marketingHeaderNavIsCurrent from './marketingHeaderNavIsCurrent';
import { DEFAULT_SITE } from './siteDefaults';
import { getSeedVarietyProductInList, getSeedVarietyVariantInList } from './seedVarietyCatalog';
import { useSeedVarietyProducts } from './useSeedVarietyProducts';

export function SeedVarietyProductRedirect() {
  const { productId } = useParams();
  const decodedProductId = productId ? decodeURIComponent(productId) : '';
  const { products, ready, site } = useSeedVarietyProducts();
  const product = getSeedVarietyProductInList(products, decodedProductId);

  if (!ready) {
    return (
      <div className="min-h-screen bg-white text-neutral-900">
        <MarketingSiteHeader
          site={site}
          idPrefix="seed-redirect"
          alwaysSolid
          isNavLinkCurrent={marketingHeaderNavIsCurrent}
        />
        <div className="flex min-h-[50vh] items-center justify-center text-neutral-500 pt-[calc(4.5rem+env(safe-area-inset-top))]">
          Loading…
        </div>
      </div>
    );
  }

  if (!product || !product.variants.length) {
    return <SeedVarietyNotFound site={site} />;
  }

  const firstId = product.variants[0].id;
  return <Navigate to={`/seed-varieties/${encodeURIComponent(decodedProductId)}/${encodeURIComponent(firstId)}`} replace />;
}

function SeedVarietyNotFound({ site: siteProp }) {
  const site =
    siteProp && typeof siteProp === 'object' ? siteProp : JSON.parse(JSON.stringify(DEFAULT_SITE));
  return (
    <div className="min-h-screen bg-white text-neutral-900">
      <MarketingSiteHeader
        site={site}
        idPrefix="seed-not-found"
        alwaysSolid
        isNavLinkCurrent={marketingHeaderNavIsCurrent}
      />
      <div className="mx-auto max-w-2xl px-4 py-20 pt-[calc(5rem+env(safe-area-inset-top))] text-center sm:px-6 sm:pt-[calc(5.5rem+env(safe-area-inset-top))]">
        <h1 className="text-2xl font-bold text-neutral-900">Variety not found</h1>
        <p className="mt-3 text-neutral-600">This specification sheet does not exist or the link is incorrect.</p>
        <Link
          to="/seed-varieties"
          className="mt-8 inline-flex text-sm font-semibold text-brand-lime no-underline hover:text-brand-lime-hover"
        >
          ← All variety sheets
        </Link>
      </div>
    </div>
  );
}

export default function SeedVarietyDetail() {
  const { productId, variantId } = useParams();
  const decodedProductId = productId ? decodeURIComponent(productId) : '';
  const decodedVariant = variantId ? decodeURIComponent(variantId) : '';
  const { products, ready, site } = useSeedVarietyProducts();
  const resolved = getSeedVarietyVariantInList(products, decodedProductId, decodedVariant);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, [decodedProductId, decodedVariant]);

  useEffect(() => {
    const base = 'Rafid Seed';
    if (!ready) return undefined;
    if (!resolved) {
      document.title = `Variety not found | ${base}`;
      return () => {
        document.title = base;
      };
    }
    document.title = `${resolved.variant.name} — ${resolved.product.categoryName} | ${base}`;
    return () => {
      document.title = base;
    };
  }, [ready, resolved]);

  if (!ready) {
    return (
      <div className="min-h-screen bg-brand-cream/40 text-neutral-900">
        <MarketingSiteHeader
          site={site}
          idPrefix="seed-detail"
          alwaysSolid
          isNavLinkCurrent={marketingHeaderNavIsCurrent}
        />
        <div className="flex min-h-[50vh] items-center justify-center text-neutral-500 pt-[calc(4.5rem+env(safe-area-inset-top))]">
          Loading…
        </div>
      </div>
    );
  }

  if (!resolved) {
    return <SeedVarietyNotFound site={site} />;
  }

  const { product, variant } = resolved;
  const { categoryName } = product;
  const { name, breeder, image, alt } = variant;
  const specs = Array.isArray(variant.specs) ? variant.specs : [];

  return (
    <div className="min-h-screen bg-brand-cream/40 text-neutral-900">
      <MarketingSiteHeader
        site={site}
        idPrefix="seed-detail"
        alwaysSolid
        isNavLinkCurrent={marketingHeaderNavIsCurrent}
      />

      <nav className="border-b border-neutral-200/80 bg-white/80 px-4 pb-3 pt-[calc(4.25rem+env(safe-area-inset-top))] text-sm text-neutral-600 sm:px-6 sm:pb-3 sm:pt-[calc(4.5rem+env(safe-area-inset-top))] lg:px-8">
        <div className="mx-auto flex max-w-shell flex-wrap items-center gap-x-2 gap-y-1">
          <Link to="/" className="font-medium text-brand-olive no-underline hover:underline">
            Home
          </Link>
          <span className="text-neutral-400" aria-hidden>
            /
          </span>
          <Link to="/seed-varieties" className="font-medium text-brand-olive no-underline hover:underline">
            Variety specs
          </Link>
          <span className="text-neutral-400" aria-hidden>
            /
          </span>
          <span className="truncate text-neutral-500">{categoryName}</span>
          <span className="text-neutral-400" aria-hidden>
            /
          </span>
          <span className="truncate text-neutral-500">{name}</span>
        </div>
      </nav>

      <main className="mx-auto max-w-shell px-4 py-10 sm:px-6 sm:py-12 lg:px-8">
        <div className="grid gap-10 lg:grid-cols-2 lg:gap-12 lg:items-start">
          <div className="overflow-hidden rounded-2xl border border-neutral-200/90 bg-white shadow-sm ring-1 ring-black/5 lg:sticky lg:top-[calc(5.25rem+env(safe-area-inset-top))]">
            <div className="aspect-square bg-neutral-100 sm:aspect-[4/3] lg:aspect-square">
              <img src={image} alt={alt || name} className="h-full w-full object-cover" />
            </div>
          </div>

          <div>
            <h1 className="text-3xl font-bold leading-tight tracking-tight text-neutral-900 sm:text-4xl">
              {categoryName}
            </h1>
            <p className="mt-2 text-xl font-semibold text-neutral-800 sm:text-2xl">{name}</p>
            <p className="mt-2 text-sm text-neutral-600">
              Breeder: <span className="font-medium text-neutral-800">{breeder}</span>
            </p>

            {product.variants.length > 1 ? (
              <div className="mt-6">
                <p className="text-xs font-semibold uppercase tracking-wide text-neutral-500">Other varieties</p>
                <ul className="mt-2 flex flex-wrap gap-2 p-0">
                  {product.variants.map((v) => {
                    const active = v.id === variant.id;
                    return (
                      <li key={v.id}>
                        {active ? (
                          <span className="inline-flex rounded-full bg-brand-forest px-3 py-1.5 text-xs font-semibold text-white">
                            {v.name}
                          </span>
                        ) : (
                          <Link
                            to={`/seed-varieties/${encodeURIComponent(product.id)}/${encodeURIComponent(v.id)}`}
                            className="inline-flex rounded-full border border-neutral-300 bg-white px-3 py-1.5 text-xs font-semibold text-neutral-800 no-underline transition-colors hover:border-brand-olive hover:text-brand-olive"
                          >
                            {v.name}
                          </Link>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>
            ) : null}

            <div className="mt-8 overflow-hidden rounded-xl border border-neutral-200/90 bg-white shadow-sm">
              <table className="w-full border-collapse text-left text-sm">
                <tbody>
                  {specs.length === 0 ? (
                    <tr>
                      <td colSpan={2} className="px-4 py-6 text-center text-sm text-neutral-500">
                        No specification rows yet.
                      </td>
                    </tr>
                  ) : null}
                  {specs.map((row, i) => (
                    <tr
                      key={`${row.label}-${i}`}
                      className={i % 2 === 0 ? 'bg-emerald-50/70' : 'bg-white'}
                    >
                      <th
                        scope="row"
                        className="w-[45%] px-4 py-3 font-semibold text-neutral-800 sm:px-5 sm:py-3.5"
                      >
                        {row.label}
                      </th>
                      <td className="px-4 py-3 text-neutral-700 sm:px-5 sm:py-3.5">{row.value}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="mt-10 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
              <Link
                to="/#contact"
                className="inline-flex min-h-[48px] items-center justify-center rounded-2xl bg-brand-lime px-6 py-3 text-sm font-semibold text-brand-forest no-underline transition-colors hover:bg-brand-lime-hover"
              >
                Request quote / contact
              </Link>
              <Link
                to="/seed-varieties"
                className="inline-flex min-h-[48px] items-center justify-center rounded-2xl border border-neutral-300 bg-white px-6 py-3 text-sm font-semibold text-neutral-800 no-underline transition-colors hover:bg-neutral-50"
              >
                ← All variety sheets
              </Link>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
